/**
 * Program Benefits 5008 — KUK "Why KUK Online" benefit cards.
 *
 * A grid of cards, each a photograph washed out behind an icon, a title and a
 * line of copy. The wash alternates by card position, which is the block's whole
 * visual character — see `ProgramBenefits5008.tsx`. Ported from
 * kuk-campus-portal's `components/Home/KeyBenefits.tsx`.
 *
 * Every field maps to a column that already exists on `program_benefits` — the
 * variant adds no schema of its own beyond widening `program_benefits.variant`.
 */

export const PROGRAM_BENEFITS_5008_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Benefit cards",
  },

  DEFAULT_ROW: {
    id: "1",
    meta_image: "",
    meta_alt_image: "",
    meta_icon: "",
    meta_title: "",
    meta_sub_title: "",
    meta_title_color: "#101112",
    meta_sub_title_color: "#101112",
    meta_priority: 1,
    meta_status: true,
  },

  PROGRAM_BENEFITS_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Why KUK {online} ?",
      description: "Wrap any phrase in { } to paint it in the highlight colour below.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title_start_color",
      label: "Heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "title_end_color",
      label: "Heading highlight colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "Applies to the { } run in the heading.",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "subtitle",
      label: "Sub-heading",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 2,
      placeholder: "e.g. Access world-class education in a flexible, immersive and active learning format.",
      description: "Hidden below 768px, as in the reference — the cards carry the message there.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "subtitle_color",
      label: "Sub-heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "desktop_background_color",
      label: "Section background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "gradient_color_1",
      label: "Card wash — odd cards (1st, 3rd, …)",
      value: "rgba(255, 255, 255, 0.60)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.60)",
      description: "The white layer over the card photo. The reference alternates this and the tint below by card position.",
      required: false,
      defaultValue: "rgba(255, 255, 255, 0.60)",
    },
    {
      fieldName: "gradient_color_3",
      label: "Card tint — odd cards",
      value: "rgba(3, 31, 68, 0.50)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(3, 31, 68, 0.50)",
      required: false,
      defaultValue: "rgba(3, 31, 68, 0.50)",
    },
    {
      fieldName: "gradient_color_2",
      label: "Card wash — even cards (2nd, 4th, …)",
      value: "rgba(255, 255, 255, 0.80)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.80)",
      required: false,
      defaultValue: "rgba(255, 255, 255, 0.80)",
    },
    {
      fieldName: "gradient_color_4",
      label: "Card tint — even cards",
      value: "rgba(3, 31, 68, 0.40)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(3, 31, 68, 0.40)",
      required: false,
      defaultValue: "rgba(3, 31, 68, 0.40)",
    },
    {
      fieldName: "pre_title_color",
      label: "Card icon colour",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      description: "Applies to icon-key icons; uploaded images keep their own colours.",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Card photo",
      fieldName: "meta_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the card background",
      defaultValue: "",
    },
    {
      header: "Card photo alt",
      fieldName: "meta_alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Card icon",
      fieldName: "meta_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      description: "Accepts an uploaded image URL OR a built-in icon name such as clock, globe, graduation.",
      defaultValue: "",
    },
    {
      header: "Card title",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Learn Your Way",
      defaultValue: "",
    },
    {
      header: "Card description",
      fieldName: "meta_sub_title",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 3,
      defaultValue: "",
    },
    {
      header: "Card title colour",
      fieldName: "meta_title_color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      header: "Card description colour",
      fieldName: "meta_sub_title_color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [],
};
